import axios from 'axios';
import { rumTrackAction } from '@nx-frontend/shared/rum';

const ATTACHMENT_CHUNK_SIZE = 5e7; // 50MB
const ATTACHMENT_REQUESTS_COUNT = 5;

interface UploadFileOptions {
  /**
   * Track upload progress
   * @param progress
   * @returns
   */
  onProgress?: (progress: { total: number; loaded: number }) => void;
  /**
   * Receives a cancel function that when called will cancel the upload
   * @param cancelFn
   * @returns
   */
  onCancelFn?: (cancelFn: () => void) => void;
  /**
   * Triggers when the file upload is not abortable anymore and user will have to wait for the upload to finish
   * @returns
   */
  onIsNotAbortable?: () => void;
  folder?: string;
}

interface UploadStartResult {
  id: string;
  partsUrls: string[];
  partSize: number;
  tempStoredfileName: string;
}

export interface AttachmentPart {
  etag: string;
  partNumber: number;
}

export interface TempStoredFile {
  filename: string;
  kind?: string;
  name: string;
  size: number;
  type: string;
  storedFile?: boolean;
}

type PromiseFunction<T = unknown> = (...args: unknown[]) => Promise<T>;

const pLimit = (concurrency: number) => {
  if (!((Number.isInteger(concurrency) || concurrency === Infinity) && concurrency > 0)) {
    throw new TypeError('Expected `concurrency` to be a number from 1 and up');
  }

  const queue: Array<() => void> = [];
  let activeCount = 0;

  const next = () => {
    activeCount--;
    if (queue.length > 0) {
      queue.shift()?.();
    }
  };

  const run = async <T>(fn: PromiseFunction<T>, resolve: (value: Promise<T>) => void, ...args: unknown[]) => {
    activeCount++;
    const result = Promise.resolve(fn(...args));
    resolve(result);
    try {
      await result;
    } catch (e) {
      /* error handling if needed */
    }
    next();
  };

  const enqueue = <T>(fn: PromiseFunction<T>, resolve: (value: Promise<T>) => void, ...args: unknown[]) => {
    queue.push(run.bind(null, fn, resolve, ...args));
    (async () => {
      await Promise.resolve();
      if (activeCount < concurrency && queue.length > 0) {
        queue.shift()?.();
      }
    })();
  };

  const generator = <T>(fn: PromiseFunction<T>, ...args: unknown[]) =>
    new Promise<Promise<T>>((resolve) => enqueue(fn, resolve, ...args));

  return Object.defineProperties(generator, {
    activeCount: { get: () => activeCount },
    pendingCount: { get: () => queue.length },
    clearQueue: {
      value: () => {
        queue.length = 0;
      },
    },
  });
};

/**
 * Uploads a file in a smart way considering its size:
 * - For files less than 50MB uses a single upload
 * - For files larger than 50MB splits the file into chunks and uploads each chunk separately utilizing S3 Mutlipart uploads.
 * @param file
 * @param options
 * @returns TempStoredFile
 * @example
 * // Basic usage
 * const fileInput = document.querySelector('input[type="file"]');
 * if (fileInput.files.length > 0) {
 *   const result = await uploadFile(fileInput.files[0]);
 *   console.log('Upload complete:', result);
 * }
 *
 * @example
 * // Using all options
 * const fileInput = document.querySelector('input[type="file"]');
 * if (fileInput.files.length > 0) {
 *   let cancelUpload: () => void;
 *
 *   const result = await uploadFile(fileInput.files[0], {
 *     // Track upload progress
 *     onProgress: (progress) => {
 *       const percentage = Math.round((progress.loaded / progress.total) * 100);
 *       console.log(`Upload progress: ${percentage}%`);
 *       progressBar.value = percentage;
 *     },
 *
 *     // Get a function to cancel the upload
 *     onCancelFn: (cancelFn) => {
 *       cancelUpload = cancelFn;
 *
 *       // Store the cancel function to use it later
 *       cancelButton.onclick = () => {
 *         cancelUpload();
 *         console.log('Upload cancelled');
 *       };
 *     },
 *
 *     // Handle when the upload can no longer be aborted
 *     onIsNotAbortable: () => {
 *       console.log('Upload is now finalizing and cannot be cancelled');
 *       cancelButton.disabled = true;
 *       cancelButton.textContent = 'Finalizing...';
 *     }
 *   });
 *
 *   if (result) {
 *     console.log('Upload complete:', result);
 *   } else {
 *     console.error('Upload failed or was cancelled');
 *   }
 * }
 */
export async function uploadFile(file: File, options: UploadFileOptions = {}): Promise<TempStoredFile | null> {
  if (file.size < ATTACHMENT_CHUNK_SIZE) {
    const tempFile = await createSmallTempStoredFile(file, options);
    if (tempFile) {
      tempFile.storedFile = true;
    }
    return tempFile;
  } else {
    const tempFile = await createLargeTempStoredFile(file, options);
    if (tempFile) {
      tempFile.storedFile = false;
    }
    return tempFile;
  }
}

async function createSmallTempStoredFile(file: File, options: UploadFileOptions = {}): Promise<TempStoredFile | null> {
  const source = axios.CancelToken.source();
  options.onCancelFn?.(() => source.cancel());
  const formData = new FormData();
  formData.append('file', file, file.name);
  if (options.folder) {
    formData.append('folder', options.folder);
  }
  try {
    const response = await axios.post(`/v3/api/web-editor/file/v2-upload`, formData, {
      onUploadProgress: (progress): void => {
        if (options.onProgress) {
          options.onProgress({
            total: progress.total as number,
            loaded: progress.loaded,
          });
        }
      },
      cancelToken: source.token,
    });
    return response.data;
  } catch (error) {
    console.log(error);
    return null;
  }
}
async function createLargeTempStoredFile(file: File, options: UploadFileOptions = {}): Promise<TempStoredFile | null> {
  let isAborted = false;
  const uploadingProgress: number[] = [];
  const onProgress = (index: number, bytes: number): void => {
    uploadingProgress[index] = bytes;
    if (options.onProgress) {
      options.onProgress({
        total: file.size,
        loaded: uploadingProgress.reduce((mem, bytes) => mem + bytes, 0),
      });
    }
  };
  const readBlob = async (index: number, step: number): Promise<Blob> => {
    return await new Promise<Blob>((resolve) => {
      const reader = new FileReader();
      const blob = file.slice(index * step, (index + 1) * step);
      reader.onload = resolve.bind(null, blob);
      reader.readAsArrayBuffer(blob);
    });
  };
  const uploadBlob = async (url: string, index: number, step: number): Promise<AttachmentPart | null> => {
    if (isAborted) {
      return null;
    }
    const blob = await readBlob(index, step);
    const source = axios.CancelToken.source();

    options.onCancelFn?.(() => {
      isAborted = true;
      source.cancel();
    });

    const response = await axios.put(url, blob, {
      headers: { 'Content-Type': undefined },
      onUploadProgress: (progress): void => {
        onProgress(index, progress.loaded);
      },
      cancelToken: source.token,
    });
    let etag = '';
    try {
      etag = JSON.parse(response.headers['etag']);
      if (!etag) {
        throw new Error('ETAG_IS_EMPTY');
      }
    } catch (err) {
      throw new Error('ETAG_IS_EMPTY');
    }
    return { etag, partNumber: index + 1 };
  };
  try {
    const emptyBlob = new Blob([], { type: 'text/plain' });
    const formDataStart = new FormData();
    formDataStart.append('file', emptyBlob, 'empty.txt');
    formDataStart.append('action', 'start');
    formDataStart.append('name', file.name);
    formDataStart.append('type', file.type);
    formDataStart.append('size', `${file.size}`);
    if (options.folder) {
      formDataStart.append('folder', options.folder);
    }
    const uploading = await axios.post<UploadStartResult>(`/v3/api/web-editor/file/v2-upload`, formDataStart);

    rumTrackAction('editor-mulitpart-upload-attachment-start', {
      numParts: uploading.data.partsUrls.length,
    });

    const limit = pLimit(ATTACHMENT_REQUESTS_COUNT);
    const limitedUpload = (url: string, index: number) => limit(() => uploadBlob(url, index, uploading.data.partSize));
    const parts = await Promise.all(uploading.data.partsUrls.map(limitedUpload));
    if (parts.some((part) => !part)) {
      console.error('MULTIPART_UPLOAD_TASK_ABORTED');
      return null;
    }
    const formDataFinish = new FormData();
    formDataFinish.append('file', emptyBlob, 'empty.txt');
    formDataFinish.append('action', 'finish');
    formDataFinish.append('parts', JSON.stringify(parts));
    formDataFinish.append('uploadingId', uploading.data.id);
    formDataFinish.append('tempStoredfileName', uploading.data.tempStoredfileName);

    options.onIsNotAbortable?.();

    const { data: tempStoredFile } = await axios.post<TempStoredFile>(
      `/v3/api/web-editor/file/v2-upload`,
      formDataFinish
    );

    rumTrackAction('editor-mulitpart-upload-attachment-finish', {
      result: tempStoredFile,
    });

    return tempStoredFile;
  } catch (error) {
    console.log(error);
    return null;
  }
}
